import React, { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Table, Button, Form, Modal, Alert, Badge, Spinner } from 'react-bootstrap';
import DesktopLayout from '../layouts/DesktopLayout';
import { AuthContext } from '../context/AuthContext';
import secureApiService from '../services/secureApi';
import { formatCompactDate } from '../utils/dateFormatter';

const INTENT_LABELS = {
  guidebook_lookup: '📖 دليل الإرشادات',
  maintenance: '🔧 الصيانة',
  housekeeping_supply: '🧹 مستلزمات التنظيف',
  early_check_in: '🚪 تسجيل دخول مبكر',
  late_check_out: '🚪 تسجيل خروج متأخر',
  task_status_query: '📋 استعلام حالة المهمة',
  safety_alert: '🚨 تنبيه سلامة',
  owner_billing: '💰 فواتير المالك',
  reservation_modify: '📅 تعديل حجز',
  guest_billing: '💳 فواتير ضيف',
  lost_found: '🔍 مفقودات',
  local_recommendations: '🗺️ توصيات محلية'
};

const MODE_LABELS = {
  auto_handle: 'تلقائي كامل',
  monitored: 'مراقب',
  alerted: 'مراقب + تنبيه',
  approval_required: 'يتطلب موافقة'
};

const MODE_COLORS = {
  auto_handle: 'success',
  monitored: 'primary',
  alerted: 'warning',
  approval_required: 'danger'
};

const IntentPoliciesPage = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [policies, setPolicies] = useState([]);
  const [error, setError] = useState(null);
  const [editingPolicy, setEditingPolicy] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (user && user.role !== 'admin') {
      navigate('/dashboard');
    }
  }, [user, navigate]);

  useEffect(() => {
    fetchPolicies();
  }, []);

  const fetchPolicies = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await secureApiService.getIntentPolicies();
      setPolicies(response.policies || []);
    } catch (err) {
      console.error('Failed to load intent policies', err);
      setError(err.response?.data?.message || 'فشل في تحميل السياسات');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (policy) => {
    setEditingPolicy({ ...policy });
    setShowEditModal(true);
  };

  const handleSave = async () => {
    if (!editingPolicy) return;

    try {
      setSaving(true);
      await secureApiService.updateIntentPolicy(editingPolicy.intent, {
        mode: editingPolicy.mode,
        notes: editingPolicy.notes
      });
      setShowEditModal(false);
      fetchPolicies(); // Refresh
    } catch (err) {
      console.error('Failed to update policy', err);
      alert(err.response?.data?.message || 'فشل في تحديث السياسة');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <DesktopLayout>
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
          <Spinner animation="border" variant="primary" />
        </div>
      </DesktopLayout>
    );
  }

  return (
    <DesktopLayout>
      <div className="container-fluid py-4">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h2>سياسات مراقبة النوايا</h2>
          <Button variant="outline-secondary" onClick={fetchPolicies} disabled={loading}>
            🔄 تحديث
          </Button>
        </div>

        {error && <Alert variant="danger">{error}</Alert>}

        <Alert variant="warning" className="mb-3">
          <strong>⚠️ وضع MVP:</strong> حالياً يتم عرض <strong>جميع المحادثات</strong> في اللوحة المباشرة بغض النظر عن إعدادات السياسة.
          التصفية تعتمد فقط على الوقت (آخر 48 ساعة افتراضياً). عندما تكون جاهزاً لتمكين التصفية الذكية بناءً على النوايا،
          ستتحكم هذه السياسات في المحادثات التي تظهر.
        </Alert>

        <Alert variant="info">
          <strong>ملاحظة:</strong> تحكم في كيفية مراقبة فريق العمليات لكل نوع من أنواع النوايا. يمكنك تغيير الوضع من "مراقب" إلى "تلقائي كامل" عندما تثبت الأداء.
        </Alert>

        <Table responsive bordered hover className="bg-white">
          <thead>
            <tr className="text-end">
              <th>النية</th>
              <th>الوضع الحالي</th>
              <th>آخر تحديث</th>
              <th>ملاحظات</th>
              <th>إجراءات</th>
            </tr>
          </thead>
          <tbody>
            {policies.map((policy) => (
              <tr key={policy.intent}>
                <td className="text-end">{INTENT_LABELS[policy.intent] || policy.intent}</td>
                <td className="text-end">
                  <Badge bg={MODE_COLORS[policy.mode]}>
                    {MODE_LABELS[policy.mode]}
                  </Badge>
                </td>
                <td className="text-end">
                  {policy.updatedAt ? formatCompactDate(policy.updatedAt) : '—'}
                </td>
                <td className="text-end" style={{ maxWidth: '300px' }}>
                  <small className="text-muted">{policy.notes || '—'}</small>
                </td>
                <td className="text-end">
                  <Button size="sm" variant="outline-primary" onClick={() => handleEdit(policy)}>
                    تعديل
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>

        {/* Edit Modal */}
        <Modal show={showEditModal} onHide={() => setShowEditModal(false)} dir="rtl">
          <Modal.Header closeButton>
            <Modal.Title>تعديل سياسة النية</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {editingPolicy && (
              <>
                <Form.Group className="mb-3">
                  <Form.Label>النية</Form.Label>
                  <Form.Control
                    type="text"
                    value={INTENT_LABELS[editingPolicy.intent] || editingPolicy.intent}
                    disabled
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>وضع المراقبة</Form.Label>
                  <Form.Select
                    value={editingPolicy.mode}
                    onChange={(e) => setEditingPolicy({ ...editingPolicy, mode: e.target.value })}
                  >
                    <option value="auto_handle">تلقائي كامل (AI يتعامل بشكل مستقل)</option>
                    <option value="monitored">مراقب (يظهر في لوحة العمليات)</option>
                    <option value="alerted">مراقب + تنبيه (إشعار للعمليات)</option>
                    <option value="approval_required">يتطلب موافقة (AI ينتظر)</option>
                  </Form.Select>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>ملاحظات</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    value={editingPolicy.notes || ''}
                    onChange={(e) => setEditingPolicy({ ...editingPolicy, notes: e.target.value })}
                    placeholder="سبب التغيير..."
                  />
                </Form.Group>
              </>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowEditModal(false)} disabled={saving}>
              إلغاء
            </Button>
            <Button variant="primary" onClick={handleSave} disabled={saving}>
              {saving ? 'جارٍ الحفظ...' : 'حفظ التغييرات'}
            </Button>
          </Modal.Footer>
        </Modal>
      </div>
    </DesktopLayout>
  );
};

export default IntentPoliciesPage;
